/**
 * `<caos-card-grid>` — the CARD body of a list surface.
 * (Catalog: Data display › CardGrid — the CARD body of a list surface — a compact card per record.)
 *
 * A responsive grid of COMPACT record cards (the reference Accounts gold standard —
 * NOT the old ~28-field Contacts dump). Each card is an object ICON tile + the record NAME (accent
 * red, bold, ellipsis-truncated) + a trailing caret, a segment/type SUB-LINE, and up to TWO labeled
 * count/rollup METRICS (e.g. "3 contacts", "2 open orders") — never the full displayed column set. The
 * card carries a FAINT `--caos-color-border` hairline at rest (NOT the strong/red border of the
 * old Contacts defect) and lifts to an accent border + a light accent wash on hover.
 *
 * Data in, events out — the same contract `<caos-data-table>` follows. The app layer supplies
 * `items` (already FLS-filtered + type-formatted; the name, sub-line, and metrics are the curated
 * compact set), and the card emits `caos-card-open` (`{ id }`) when a card is activated — the host
 * routes it through the app's History-API navigation to open the record.
 *
 * Styling seams: `::part(grid)`, `::part(card)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import {
  DEFAULT_OVERSCAN,
  VIRTUAL_THRESHOLD,
  virtualWindow,
  wholeList,
  type VirtualWindow,
  type VirtualWindowInput,
} from '../virtual-window.js';
import { visibleViewport } from '../anchored-panel.js';
import { isIconName, makeIcon } from './icons.js';
// The card's avatar is the library's own avatar, which already solves the fallback ladder
// (picture, then initials, then a glyph) and takes its diameter from the platform size scale.
// Registered here rather than left to whatever else happened to import it first.
import './avatar.js';

/** One field row (label + value, optional link). Retained for the split-view detail/secondary
 *  lists; the COMPACT card no longer renders a field list (it uses `subtitle` + `metrics`). */
export interface CaosCardField {
  label: string;
  value: string;
  href?: string;
}

/** One labeled count/rollup metric on a compact card (e.g. value "3" + label "contacts"). */
export interface CaosCardMetric {
  value: string;
  label: string;
}

/**
 * A picture of the record itself, for the card's leading mark (CAOS-601).
 *
 * Not a second avatar: it is the input to the library's `<caos-avatar>`, which decides between the
 * picture, the initials and the fallback glyph. `name` is what the initials are taken from and
 * what a screen reader hears, so a card with an avatar and no name is decorative rather than
 * mislabelled.
 */
export interface CaosCardAvatar {
  /** The picture, if the record has one. A broken link falls through to the initials. */
  src?: string;
  /** Whose or what it is — the initials come from this, and so does the accessible name. */
  name?: string;
}

/** One COMPACT card — a record: its name, an optional segment/type sub-line, and up to two
 *  count/rollup metrics. `href` is the record's own route (the card opens it). */
export interface CaosCardItem {
  id: string;
  title: string;
  href?: string;
  /** The segment/type sub-line under the name (e.g. industry, role, status). */
  subtitle?: string;
  /** Up to two labeled count/rollup metrics (e.g. "3 contacts", "2 open orders"). */
  metrics?: CaosCardMetric[];
  /**
   * CAOS-601 — the mark this record's KIND carries, by its name in the platform vocabulary. A grid
   * of accounts gets the building, a grid of people gets the person, and a mixed grid can say which
   * is which; before this, every card in every grid drew the same generic document.
   *
   * A plain string rather than the vocabulary's own type, because this arrives as DATA — from an
   * object's metadata, not from a line somebody compiled — and a name the vocabulary does not draw
   * falls back to the generic record glyph rather than leaving an empty tile.
   */
  icon?: string;
  /**
   * CAOS-601 — a picture of the record itself, which takes the tile INSTEAD of a glyph: a person's
   * photograph, an organisation's logo. It wins over `icon`, because a picture of the actual thing
   * says more than the mark for its kind, and a card has room for exactly one leading mark.
   */
  avatar?: CaosCardAvatar;
}
const sheet = makeSheet(`
  :host { display: block; }

  /* CAOS-652 — padding, gaps and radii come from the spacing and radius scales. The pixel figures
     left in this sheet are intrinsic sizes (the track minimum, the icon tile and its glyphs, the
     lazy-load sentinel), hairline borders and type sizes. */

  /* Gold-standard Accounts grid: auto-fill 320px tracks on the scale's 16px gutter. The track is measured off the
     reference card, and it is a MINIMUM for a reason — a narrower track buys another column at the
     cost of clipping the one thing a card exists to show, which is the record's name. */
  .grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    gap: var(--caos-space-4);
  }

  /* The card is a real <button> (reference parity): faint hairline at rest, accent on hover. */
  .card {
    display: flex;
    flex-direction: column;
    gap: var(--caos-space-3);
    padding: var(--caos-space-4);
    text-align: left;
    cursor: pointer;
    font-family: var(--caos-font);
    background: var(--caos-color-surface);
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius-lg);
    box-shadow: var(--caos-shadow-card);
    transition: border-color 0.12s ease, background 0.12s ease;
  }
  .card:hover { border-color: var(--caos-color-accent); background: var(--caos-accent-wash); }
  .card:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }

  /* Head: object icon tile · (name over sub-line) · trailing caret. */
  .head { display: flex; align-items: flex-start; gap: var(--caos-space-3); }
  /* Object icon tile — the CARD view HAS an icon (unlike the record highlights band): an accent
     glyph on a soft accent tile with an accent hairline (reference Accounts card, 34×34). */
  .icon {
    flex: 0 0 auto;
    width: 34px;
    height: 34px;
    display: grid;
    place-items: center;
    border-radius: var(--caos-radius-sm);
    background: color-mix(in srgb, var(--caos-color-accent) 14%, transparent);
    border: 1px solid color-mix(in srgb, var(--caos-color-accent) 35%, transparent);
  }
  .icon svg { display: block; width: 16px; height: 16px; color: var(--caos-color-accent); }
  /* CAOS-601 — when the record has a picture of its own, the avatar takes the tile's place. Its
     diameter comes from the platform size scale rather than matching this file's 34px tile: the
     scale is the platform's answer to how big a mark is, and the head row aligns to the top, so the
     couple of pixels between them disturb nothing. */
  .head caos-avatar { flex: 0 0 auto; }
  .headtext { min-width: 0; flex: 1 1 auto; display: flex; flex-direction: column; gap: var(--caos-space-1); }
  .name {
    min-width: 0;
    color: var(--caos-color-accent);
    font-size: 15px;
    font-weight: 700;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  /* Segment/type sub-line — a faint single line; long values truncate, never wrap. */
  .subtitle {
    min-width: 0;
    color: var(--caos-color-text-ghost);
    font-size: 11px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .chevron { flex: 0 0 auto; align-self: center; width: 16px; height: 16px; color: var(--caos-color-text-ghost); }
  .chevron svg { display: block; width: 100%; height: 100%; }

  /* Metric row — up to two labeled count/rollup stats ("3 contacts"). */
  .metrics { display: flex; flex-wrap: wrap; gap: var(--caos-space-4); }
  .metric { display: inline-flex; align-items: baseline; gap: var(--caos-space-1); font-size: 12px; color: var(--caos-color-text-muted); }
  .metric .mval { color: var(--caos-color-text); font-variant-numeric: tabular-nums; font-weight: 600; }

  .empty { padding: var(--caos-space-2); font-size: 13px; color: var(--caos-color-text-muted); }

  /* End-of-grid sentinel for lazy loading — a zero-height marker the IntersectionObserver
     watches; when it scrolls into view the next batch of cards is appended. */
  .sentinel { width: 100%; height: 1px; }

  /* CAOS-601 — the space the cards that are NOT in the DOM would have taken. Without these the
     scrollbar describes the handful of cards that happen to be rendered rather than the list, and
     the scroll position jumps the moment the window moves. They span every column, so a grid of
     three tracks does not try to lay a spacer out as a card. */
  .spacer { grid-column: 1 / -1; }
`);

/** Default cards rendered per lazy batch (initial paint + each infinite-scroll append). */
const DEFAULT_BATCH_SIZE = 24;

export class CaosCardGrid extends CaosElement {
  private gridEl: HTMLDivElement;
  private _items: CaosCardItem[] = [];
  /** How many cards to reveal per batch (initial paint + each subsequent scroll append). */
  private _batchSize = DEFAULT_BATCH_SIZE;
  /** How many of `_items` are currently painted into the grid. */
  private _rendered = 0;
  /** End-of-grid marker the observer watches; entering view appends the next batch. */
  private sentinel: HTMLDivElement;
  private observer: IntersectionObserver | null = null;

  // ── CAOS-601, virtualisation ──────────────────────────────────────────────────────────────────
  // Behaviour, never a choice. A page author places this grid; whether the list it is given has
  // twelve records or twelve thousand is not something they should have to have known in advance,
  // and is not a reason to reach for a different component.
  /** How tall one ROW of cards is, measured off what has been painted. 0 until something has. */
  private rowHeight = 0;
  /** The slice currently in the DOM. */
  private window: VirtualWindow = wholeList(0);
  /** Coalesces the scroll/resize storm into one repaint per frame. */
  private frame: number | null = null;
  private readonly spacerTop: HTMLDivElement;
  private readonly spacerBottom: HTMLDivElement;

  private readonly onViewportChange = (): void => {
    if (this.frame !== null) return;
    const view = this.ownerDocument.defaultView;
    if (!view) return;
    this.frame = view.requestAnimationFrame(() => {
      this.frame = null;
      this.repaintIfWindowMoved();
    });
  };

  constructor() {
    super(sheet);
    this.gridEl = document.createElement('div');
    this.gridEl.className = 'grid';
    this.gridEl.setAttribute('part', 'grid');
    this.root.append(this.gridEl);

    // The sentinel lives after the grid; when it scrolls into view the next batch is appended.
    this.sentinel = document.createElement('div');
    this.sentinel.className = 'sentinel';
    this.sentinel.setAttribute('aria-hidden', 'true');
    this.root.append(this.sentinel);

    this.spacerTop = this.buildSpacer();
    this.spacerBottom = this.buildSpacer();
  }

  /** One of the two empty bands standing in for the cards that are not in the DOM. */
  private buildSpacer(): HTMLDivElement {
    const el = document.createElement('div');
    el.className = 'spacer';
    el.setAttribute('aria-hidden', 'true');
    return el;
  }

  connectedCallback(): void {
    this.setupObserver();
    const view = this.ownerDocument.defaultView;
    view?.addEventListener('scroll', this.onViewportChange, { passive: true });
    view?.addEventListener('resize', this.onViewportChange, { passive: true });
    this.render();
  }

  disconnectedCallback(): void {
    this.observer?.disconnect();
    this.observer = null;
    const view = this.ownerDocument.defaultView;
    view?.removeEventListener('scroll', this.onViewportChange);
    view?.removeEventListener('resize', this.onViewportChange);
    if (this.frame !== null) {
      view?.cancelAnimationFrame(this.frame);
      this.frame = null;
    }
  }

  /**
   * What the grid can see about itself right now.
   *
   * Every number here is read off the live layout, and every one of them can be absent — before a
   * first paint, in a container that is not on screen, in a document with no view. `virtualWindow`
   * answers "render everything" to all of those, so an unmeasurable grid is a slow grid and never
   * an empty one.
   */
  private metrics(): VirtualWindowInput {
    const view = this.ownerDocument.defaultView;
    const rect = this.gridEl.getBoundingClientRect();
    return {
      count: this._items.length,
      rowHeight: this.rowHeight,
      // The VISIBLE viewport (CAOS-659): the root element's client height, which leaves the
      // scrollbar out. The window's own reported height counts it, and a height a few pixels too
      // tall quietly asks for a row nobody can see.
      viewport: view ? visibleViewport(view).height : 0,
      // How far the top of the list has travelled off the top of the viewport.
      scrollTop: Math.max(0, -rect.top),
      columns: this.columnCount(),
      overscan: DEFAULT_OVERSCAN,
    };
  }

  /** How many cards the grid is currently laying out per row, read from the track list it resolved. */
  private columnCount(): number {
    const view = this.ownerDocument.defaultView;
    const tracks = view?.getComputedStyle(this.gridEl).gridTemplateColumns ?? '';
    const n = tracks.trim() === '' || tracks === 'none' ? 0 : tracks.trim().split(/\s+/).length;
    return n > 0 ? n : 1;
  }

  /** Measure one row off what was painted: a card's own height plus the gap under it. */
  private measureRowHeight(): void {
    const card = this.gridEl.querySelector<HTMLElement>('.card');
    if (!card) return;
    const height = card.offsetHeight;
    if (!(height > 0)) return;
    const view = this.ownerDocument.defaultView;
    const gap = parseFloat(view?.getComputedStyle(this.gridEl).rowGap ?? '') || 0;
    this.rowHeight = height + gap;
  }

  /** Repaint only when the band has actually moved — a scroll inside one window changes nothing. */
  private repaintIfWindowMoved(): void {
    if (this._items.length === 0) return;
    const next = virtualWindow(this.metrics());
    if (next.start === this.window.start && next.end === this.window.end) return;
    this.paint(next);
  }

  /** Cards (data in — the access-filtered, formatted set is supplied by the app). Setting a fresh
   *  list restarts lazy rendering from the first batch; scrolling reveals the rest (no item dropped). */
  get items(): CaosCardItem[] {
    return this._items;
  }
  set items(list: CaosCardItem[]) {
    this._items = Array.isArray(list) ? list : [];
    this.render();
  }

  /** Cards revealed per lazy batch (default 24). Clamped to a sane minimum of 1. */
  get batchSize(): number {
    return this._batchSize;
  }
  set batchSize(n: number) {
    const v = Math.max(1, Math.floor(Number(n)));
    if (Number.isFinite(v) && v > 0) {
      this._batchSize = v;
      this.render();
    }
  }

  private emitOpen(id: string): void {
    this.dispatchEvent(new CustomEvent('caos-card-open', { bubbles: true, composed: true, detail: { id } }));
  }

  /** Lazily observe the sentinel. A generous rootMargin pre-loads the next batch just before it
   *  reaches the viewport edge, so scrolling stays smooth. Guarded for non-DOM/test environments. */
  private setupObserver(): void {
    if (this.observer || typeof IntersectionObserver === 'undefined') return;
    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.appendNextBatch();
        }
      },
      { root: null, rootMargin: '400px 0px', threshold: 0 },
    );
  }

  /**
   * Paint from scratch.
   *
   * A list short enough not to be worth windowing is painted whole, exactly as it always was. A
   * long one is painted at `batchSize` first — the only honest thing to do before anything has
   * been measured, since the window needs a row height and a row height needs a rendered row — and
   * then re-windowed against the real measurement.
   */
  private render(): void {
    this.gridEl.replaceChildren();
    this._rendered = 0;
    this.rowHeight = 0;

    if (this._items.length === 0) {
      this.window = wholeList(0);
      const empty = document.createElement('p');
      empty.className = 'empty';
      empty.textContent = 'Nothing to show yet.';
      this.gridEl.append(empty);
      this.updateSentinel();
      return;
    }

    if (this._items.length < VIRTUAL_THRESHOLD) {
      this.paint(wholeList(this._items.length));
      return;
    }

    this.paint({ start: 0, end: Math.min(this._items.length, this._batchSize), leading: 0, trailing: 0, windowed: true });
    this.measureRowHeight();
    this.repaintIfWindowMoved();
  }

  /** Put one window of cards in the DOM, with the space the rest would have taken above and below. */
  private paint(win: VirtualWindow): void {
    this.window = win;
    const cards: HTMLElement[] = [];
    for (let i = win.start; i < win.end; i++) {
      const item = this._items[i];
      if (item) cards.push(this.card(item));
    }

    if (win.windowed) {
      this.spacerTop.style.height = `${win.leading}px`;
      this.spacerBottom.style.height = `${win.trailing}px`;
      this.gridEl.replaceChildren(this.spacerTop, ...cards, this.spacerBottom);
    } else {
      this.gridEl.replaceChildren(...cards);
    }

    this._rendered = win.end;
    this.updateSentinel();
    if (this.rowHeight === 0) this.measureRowHeight();
  }

  /**
   * The sentinel's own path: extend the window forward by another batch.
   *
   * This is what keeps the grid working where the measurement never arrives — a browser whose
   * layout reports nothing useful, or a container the grid cannot see the top of. The window then
   * grows the way it always did rather than standing still, so the reader can still reach the end
   * of the list; it is the slow path, not the broken one.
   */
  private appendNextBatch(): void {
    if (this._rendered >= this._items.length) {
      this.updateSentinel();
      return;
    }
    // A measured grid moves its band on scroll instead, and the sentinel only ever sits under the
    // last card of a full list, so extending here would undo the window it just computed.
    if (this.window.windowed && this.rowHeight > 0) {
      this.repaintIfWindowMoved();
      return;
    }
    const end = Math.min(this._rendered + this._batchSize, this._items.length);
    this.paint({ start: 0, end, leading: 0, trailing: 0, windowed: end < this._items.length });
  }

  /** Keep the sentinel at the grid's end and (re)observe it while items remain. Re-observing after
   *  each batch re-fires the callback when a short first batch left the sentinel already in view,
   *  so the viewport fills without a scroll; once everything is painted the sentinel is released. */
  private updateSentinel(): void {
    this.root.append(this.sentinel);
    if (!this.observer) return;
    this.observer.unobserve(this.sentinel);
    if (this._rendered < this._items.length) this.observer.observe(this.sentinel);
  }

  /** Build one COMPACT record card: icon tile · name + sub-line · caret, then a metrics row. */
  private card(item: CaosCardItem): HTMLButtonElement {
    const card = document.createElement('button');
    card.type = 'button';
    card.className = 'card';
    card.setAttribute('part', 'card');
    card.dataset.recordId = item.id;
    card.addEventListener('click', () => this.emitOpen(item.id));

    const head = document.createElement('div');
    head.className = 'head';
    head.append(this.leadingMark(item));

    const text = document.createElement('div');
    text.className = 'headtext';
    const name = document.createElement('span');
    name.className = 'name';
    name.textContent = item.title;
    name.title = item.title;
    text.append(name);
    if (item.subtitle) {
      const sub = document.createElement('span');
      sub.className = 'subtitle';
      sub.textContent = item.subtitle;
      sub.title = item.subtitle;
      text.append(sub);
    }
    head.append(text, this.chevron());
    card.append(head);

    if (item.metrics && item.metrics.length) {
      const metrics = document.createElement('div');
      metrics.className = 'metrics';
      for (const m of item.metrics) {
        const metric = document.createElement('span');
        metric.className = 'metric';
        const val = document.createElement('span');
        val.className = 'mval';
        val.textContent = m.value;
        metric.append(val, document.createTextNode(' ' + m.label));
        metrics.append(metric);
      }
      card.append(metrics);
    }

    return card;
  }

/**
   * The card's leading mark (CAOS-601). A picture of the record if it has one, otherwise the glyph
   * for its kind, otherwise the generic record glyph — in that order, because each step says less
   * about this particular record than the one before it.
   */
  private leadingMark(item: CaosCardItem): HTMLElement {
    return item.avatar ? this.avatarMark(item.avatar) : this.iconTile(item.icon);
  }

  /** The record's own picture, drawn by the library's avatar so the fallback ladder is solved once. */
  private avatarMark(avatar: CaosCardAvatar): HTMLElement {
    const el = document.createElement('caos-avatar');
    if (avatar.src) el.setAttribute('src', avatar.src);
    // With no name there are no initials and nothing to announce, so the avatar stays decorative
    // rather than being given the record's title twice over — the card's name already says it.
    if (avatar.name) el.setAttribute('name', avatar.name);
    el.setAttribute('size', 'md');
    return el;
  }

  /** The object icon tile — the glyph for this record's KIND on an accent-tinted tile (the CARD
   *  view HAS an icon), falling back to the generic record mark when the card does not say. */
  private iconTile(name?: string): HTMLSpanElement {
    const tile = document.createElement('span');
    tile.className = 'icon';
    tile.setAttribute('aria-hidden', 'true');
    // CAOS-602 — the vocabulary's `record`, whose own catalogue line reads "generic record glyph".
    // This file drew a different document to mean the identical thing, which is how one vocabulary
    // ends up with two marks for a record and no way to tell which a surface will show you.
    tile.append(makeIcon(name !== undefined && isIconName(name) ? name : 'record'));
    return tile;
  }

  private chevron(): HTMLSpanElement {
    const span = document.createElement('span');
    span.className = 'chevron';
    span.setAttribute('aria-hidden', 'true');
    span.append(makeIcon('chevron-right'));
    return span;
  }
}

define('caos-card-grid', CaosCardGrid);

declare global {
  interface HTMLElementTagNameMap {
    'caos-card-grid': CaosCardGrid;
  }
}
